<template>
  <transition name="ph-modal-fade">
    <div
      v-if="open"
      ref="backdrop"
      class="ph-modal-backdrop"
      @click.self="onClose"
      @keydown.esc.stop="onClose"
    >
      <div
        class="ph-modal phd-ent-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="phd-ent-title"
        tabindex="-1"
        ref="modal"
      >
        <button
          type="button"
          class="ph-modal-close"
          @click="onClose"
          aria-label="Close"
          :disabled="submitting"
        >×</button>

        <header class="ph-modal-header">
          <span class="ph-modal-eyebrow">Enterprise</span>
          <h2 id="phd-ent-title">Let's talk volume.</h2>
          <p>For teams over 2,500 employees we put together a custom plan — better unit pricing, dedicated success, and onboarding support. Drop your details and we'll reach out within one business day.</p>
        </header>

        <form v-if="!success" class="ph-modal-form" @submit.prevent="submit">
          <div class="ph-field-row">
            <label class="ph-field">
              <span class="ph-field-label">First name *</span>
              <input
                type="text"
                v-model="form.first_name"
                class="ph-field-input"
                autocomplete="given-name"
                required
              />
            </label>
            <label class="ph-field">
              <span class="ph-field-label">Last name *</span>
              <input
                type="text"
                v-model="form.last_name"
                class="ph-field-input"
                autocomplete="family-name"
                required
              />
            </label>
          </div>

          <div class="ph-field-row">
            <label class="ph-field">
              <span class="ph-field-label">Work email *</span>
              <input
                type="email"
                v-model="form.email"
                class="ph-field-input"
                autocomplete="email"
                required
              />
            </label>
            <label class="ph-field">
              <span class="ph-field-label">Company *</span>
              <input
                type="text"
                v-model="form.company"
                class="ph-field-input"
                autocomplete="organization"
                required
              />
            </label>
          </div>

          <label class="ph-field ph-field--full">
            <span class="ph-field-label">Tell us about your team *</span>
            <textarea
              v-model="form.message"
              class="ph-field-input phd-ent-textarea"
              rows="4"
              placeholder="How many employees, how many locations, what training do you need?"
              required
            ></textarea>
          </label>

          <button
            type="submit"
            class="ph-btn ph-btn--checkout ph-modal-cta"
            :disabled="!canSubmit || submitting"
          >
            <span v-if="submitting">Submitting…</span>
            <span v-else>Request enterprise pricing →</span>
          </button>

          <div v-if="error" class="ph-demo-error" role="alert" aria-live="assertive">{{ error }}</div>
        </form>

        <div v-else class="phd-ent-success" role="status" aria-live="assertive">
          <div class="phd-ent-success-icon" aria-hidden="true">✓</div>
          <strong>Got it.</strong>
          <p>We'll reach out at <strong>{{ form.email }}</strong> within one business day with a custom proposal.</p>
          <button type="button" class="ph-btn ph-btn--ghost" @click="onClose">Close</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from 'axios';

import teleportToBody from '../../mixins/teleportToBody';

const API_BASE = (process.env.VUE_APP_API_URL || 'http://localhost:8000/').replace(/\/$/, '');

export default {
  name: 'EnterpriseRequestModal',
  mixins: [teleportToBody],

  props: {
    open: { type: Boolean, default: false },
    prefill: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      submitting: false,
      success: false,
      error: '',
      form: {
        first_name: '',
        last_name: '',
        email: '',
        company: '',
        message: '',
      },
    };
  },

  computed: {
    canSubmit() {
      const f = this.form;
      return !!f.first_name.trim()
        && !!f.last_name.trim()
        && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((f.email || '').trim())
        && !!f.company.trim()
        && !!f.message.trim();
    },
  },

  watch: {
    open(val) {
      if (val) {
        this.applyPrefill();
        this.success = false;
        this.error = '';
        this.$nextTick(() => {
          if (this.$refs.modal && typeof this.$refs.modal.focus === 'function') {
            this.$refs.modal.focus();
          }
        });
      }
    },
  },

  methods: {
    applyPrefill() {
      const p = this.prefill || {};
      if (!this.form.first_name && p.first_name) this.form.first_name = p.first_name;
      if (!this.form.last_name && p.last_name) this.form.last_name = p.last_name;
      if (!this.form.email && p.email) this.form.email = p.email;
      if (!this.form.company && p.company) this.form.company = p.company;
      // Seed the message with the buyer's current sliders so sales has
      // signal even if the buyer doesn't add detail. Only when message is
      // still blank.
      if (!this.form.message && (p.users || p.locations)) {
        const parts = [];
        if (p.users) parts.push(`${p.users} employees`);
        if (p.locations) parts.push(`${p.locations} location${p.locations === 1 ? '' : 's'}`);
        this.form.message = parts.join(', ') + '.';
      }
    },

    onClose() {
      if (this.submitting) return;
      this.$emit('close');
    },

    async submit() {
      if (!this.canSubmit || this.submitting) return;
      this.submitting = true;
      this.error = '';
      try {
        await axios.post(`${API_BASE}/user/send-enterprise-plan-request`, {
          first_name: this.form.first_name,
          last_name: this.form.last_name,
          email: this.form.email,
          company: this.form.company,
          message: this.form.message,
        });
        this.success = true;
        this.$emit('submitted', { ...this.form });
      } catch (e) {
        if (e?.response?.status === 422 && e.response.data) {
          // Surface the first validation message (backend returns
          // { field: ["error"] } shape from sendEnterprisePlan).
          const data = e.response.data;
          const firstKey = Object.keys(data).find((k) => Array.isArray(data[k]) && data[k][0]);
          this.error = firstKey ? data[firstKey][0] : 'Please double-check your details and try again.';
        } else {
          this.error = 'Could not submit your request right now. Please try again or email enterprise@homeoftraining.com.';
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<!-- Modal infrastructure: imported globally in main.js. -->
<style lang="scss" scoped>
.phd-ent-modal {
  max-width: 580px;
}

.phd-ent-textarea {
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  line-height: 1.45;
  padding: 12px 14px;
}

.phd-ent-success {
  text-align: center;
  padding: 24px 8px 8px;

  .phd-ent-success-icon {
    width: 48px;
    height: 48px;
    margin: 0 auto 12px;
    border-radius: 50%;
    background: rgba(46, 204, 113, 0.14);
    color: #1f9d55;
    font-size: 24px;
    line-height: 48px;
    font-weight: 800;
  }
  strong {
    display: block;
    font-size: 18px;
    line-height: 22px;
    color: #1a1a1a;
    margin-bottom: 8px;
  }
  p {
    font-size: 14px;
    line-height: 20px;
    color: #555;
    margin: 0 0 16px;
  }
}
</style>
